import { Nullable, SimpleImage } from '@boss/types/b2b-b2c';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import NextImage from 'next/image';
import { twMerge } from 'tailwind-merge';

import Image from '../Image';
import Link from '../Link';

type HighlightProps = {
  type: 'primary' | 'secondary';
  label: string;
  slug?: string;
  image?: SimpleImage;
  closeMenu: (hasLink: boolean) => void;
};

type MainNavItemProps = {
  children: string;
  href?: Nullable<string>;
  hasChildren: boolean;
  onClick: () => void;
  active: boolean;
  type: 'primary' | 'secondary';
  className?: string;
  role?: string;
  tabIndex?: number;
};

type SubNavLinkProps = {
  closeMenu: () => void;
  label: string;
  active: boolean;
  type: 'primary' | 'secondary';
  icon?: Nullable<IconProp>;
  href?: Nullable<string>;
  hasChildren: boolean;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
};

export const Highlight = ({ closeMenu, label, slug, image, type }: HighlightProps) => {
  if (!image) {
    return <span>{label}</span>;
  }
  if (!slug) {
    return null;
  }

  if (type === 'secondary') {
    return (
      <Link className="block overflow-hidden bg-white" data-card href={slug} onClick={() => closeMenu(!!slug)}>
        <div className="relative aspect-[16/9] overflow-hidden">
          <Image alt={image.imageAlt} className="z-0 object-cover" src={image.imageUrl} />
        </div>
        <h6 className="text-green-dark px-3 py-3">{label}</h6>
      </Link>
    );
  }

  return (
    <Link
      className={`rounded-2.5 relative block aspect-[5/4] max-w-60 overflow-hidden before:absolute before:z-10 before:h-full before:w-full before:bg-[linear-gradient(360deg,_rgba(0,0,0,0.2)_-68%,_rgba(255,255,255,0)_269%)] before:content-['']`}
      href={slug}
      onClick={() => closeMenu(!!slug)}
    >
      <Image alt={image.imageAlt} className="z-0 object-cover" src={image.imageUrl} />
      <h4 className="absolute bottom-[0px] z-20 pb-2 pl-2.5 pr-4 text-white">{label}</h4>
    </Link>
  );
};

export const MainNavItem = ({
  active: activeLink,
  href,
  children,
  hasChildren,
  onClick,
  type,
  role,
  tabIndex,
}: MainNavItemProps) => {
  const { classes, activeClass } = {
    primary: {
      classes:
        'group-hover:font-bold before:block before:h-0 before:content-[attr(title)] before:font-bold before:overflow-hidden before:opacity-0',
      activeClass: 'font-bold',
    },
    secondary: {
      classes: '',
      activeClass: 'text-green',
    },
  }[type];

  const commonClasses = `group relative mx-2 flex items-center pb-6 ${hasChildren ? 'cursor-pointer' : ''}`;

  const content = (
    <>
      <span
        className={`inline-block ${classes} ${activeLink && hasChildren ? activeClass : ''} ${
          type === 'secondary' ? 'group-hover:text-green' : ''
        }`}
        title={children}
      >
        {children}
      </span>
      {hasChildren && (
        <FontAwesomeIcon
          aria-hidden="true"
          className={MainNavIconStyle({ type, active: !!activeLink, hasChildren })}
          icon={faAngleDown}
        />
      )}
      {type === 'primary' ? (
        <NextImage
          alt=""
          className={`object-fit absolute bottom-0 h-3 w-44 opacity-0 group-hover:opacity-100`}
          height={7}
          role="presentation"
          src="/underline_nav.png"
          width={175}
        />
      ) : (
        <div
          className={`bg-green absolute inset-x-0 top-1/2 h-1 opacity-0 group-hover:opacity-100 ${
            activeLink ? 'opacity-100' : ''
          }`}
        />
      )}
    </>
  );

  if (hasChildren) {
    return (
      <button
        aria-expanded={activeLink}
        aria-haspopup="true"
        className={commonClasses}
        onClick={onClick}
        role={role}
        tabIndex={tabIndex}
        type="button"
      >
        {content}
      </button>
    );
  }

  if (href) {
    return (
      <Link
        aria-current={activeLink ? 'page' : undefined}
        className={commonClasses}
        href={href}
        role={role}
        tabIndex={tabIndex}
      >
        {content}
      </Link>
    );
  }

  return <span className={commonClasses}>{content}</span>;
};

const SubNavLinkIconStyle = cva('', {
  variants: {
    type: {
      primary: 'ml-4.25',
      secondary: 'ml-auto text-green',
    },
  },
});

const MainNavIconStyle = cva('ml-2 mt-0.5 flex text-xl', {
  variants: {
    type: {
      primary: '',
      secondary: 'group-hover:text-green',
    },
    active: {
      true: '',
      false: '',
    },
    hasChildren: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [{ type: 'secondary', active: true, hasChildren: true, className: 'text-green' }],
  defaultVariants: {
    type: 'primary',
    active: false,
    hasChildren: false,
  },
});

const SubNavLinkStyle = cva('group z-10 flex w-full items-center justify-between', {
  variants: {
    type: {
      secondary: '',
      primary: '',
    },
    active: {
      true: '',
      false: '',
    },
    hasChildren: {
      true: 'pr-3',
      false: 'pr-10',
    },
  },
  compoundVariants: [
    {
      type: 'secondary',
      active: true,
      className: 'hover:bg-green-dark hover:text-white group-hover:text-white',
    },
  ],
});

export const SubNavLink = ({
  className,
  href,
  label,
  icon,
  type,
  active,
  hasChildren,
  closeMenu,
  onKeyDown,
}: SubNavLinkProps) => {
  const content = (
    <span className={twMerge(SubNavLinkStyle({ type, active, hasChildren }), className)}>
      <span className="relative z-10 ml-4 truncate">
        {type === 'secondary' && icon && (
          <FontAwesomeIcon className="relative z-10 mr-2 hover:text-white focus:text-white" icon={icon} />
        )}
        <span>{label}</span>
      </span>
      {hasChildren && <FontAwesomeIcon className={SubNavLinkIconStyle({ type })} icon={faChevronRight} />}
    </span>
  );

  return href ? (
    <Link className={className} href={href} onClick={closeMenu} onKeyDown={onKeyDown}>
      {content}
    </Link>
  ) : (
    <button className={className} onKeyDown={onKeyDown} type="button">
      {content}
    </button>
  );
};
